import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '../../ui/dialog';
import { 
  Alert, 
  AlertDescription 
} from '../../ui/alert';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff,
  Server,
  Settings,
  Monitor,
  Save,
  X,
  Check,
  CheckCircle,
  AlertCircle,
  Package,
  FolderOpen,
  Globe,
  Shield,
  Zap,
  Crown,
  TrendingUp,
  Building,
  Code
} from 'lucide-react';
import { API_URLS } from '../../../config/api';

const AdminHostingPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price_monthly: '',
    price_yearly: '',
    original_price_monthly: '',
    original_price_yearly: '',
    discount: 0,
    color: 'from-blue-500 to-cyan-500',
    bg_color: 'bg-blue-50',
    icon: 'Server',
    is_popular: false,
    description: '',
    features: [{ name: '', included: true }],
    additional_features: [{ name: '', included: true }],
    specs: { websites: '', ram: '', cpu: '', storage: '' },
    sort_order: 0,
    url: ''
  });

  const iconOptions = [
    'Server', 'Settings', 'Monitor', 'Package', 'Globe', 'Shield', 'Zap', 'Crown', 'TrendingUp', 'Building'
  ];

  const categoryColorOptions = [
    { value: 'bg-gradient-to-r from-blue-500 to-cyan-500', label: 'Mavi', bg: 'bg-blue-50' },
    { value: 'bg-gradient-to-r from-purple-500 to-pink-500', label: 'Mor', bg: 'bg-purple-50' },
    { value: 'bg-gradient-to-r from-green-500 to-emerald-500', label: 'Yeşil', bg: 'bg-green-50' },
    { value: 'bg-gradient-to-r from-orange-500 to-red-500', label: 'Turuncu', bg: 'bg-orange-50' },
    { value: 'bg-gradient-to-r from-indigo-500 to-purple-500', label: 'İndigo', bg: 'bg-indigo-50' },
    { value: 'bg-gradient-to-r from-pink-500 to-rose-500', label: 'Pembe', bg: 'bg-pink-50' },
    { value: 'bg-gradient-to-r from-gray-600 to-gray-800', label: 'Gri', bg: 'bg-gray-50' },
    { value: 'bg-gradient-to-r from-teal-500 to-cyan-500', label: 'Turkuaz', bg: 'bg-teal-50' },
    { value: 'bg-gradient-to-r from-yellow-500 to-orange-500', label: 'Sarı', bg: 'bg-yellow-50' },
    { value: 'bg-gradient-to-r from-red-500 to-pink-500', label: 'Kırmızı', bg: 'bg-red-50' }
  ];

  const packageColorOptions = [
    { value: 'from-blue-400 to-cyan-400', label: 'Mavi (Açık)', bg: 'bg-blue-50' },
    { value: 'from-purple-400 to-pink-400', label: 'Mor (Açık)', bg: 'bg-purple-50' },
    { value: 'from-green-400 to-emerald-400', label: 'Yeşil (Açık)', bg: 'bg-green-50' },
    { value: 'from-orange-400 to-red-400', label: 'Turuncu (Açık)', bg: 'bg-orange-50' },
    { value: 'from-indigo-400 to-purple-400', label: 'İndigo (Açık)', bg: 'bg-indigo-50' },
    { value: 'from-pink-400 to-rose-400', label: 'Pembe (Açık)', bg: 'bg-pink-50' },
    { value: 'from-gray-500 to-gray-700', label: 'Gri (Açık)', bg: 'bg-gray-50' },
    { value: 'from-teal-400 to-cyan-400', label: 'Turkuaz (Açık)', bg: 'bg-teal-50' },
    { value: 'from-yellow-400 to-orange-400', label: 'Sarı (Açık)', bg: 'bg-yellow-50' },
    { value: 'from-red-400 to-pink-400', label: 'Kırmızı (Açık)', bg: 'bg-red-50' },
    { value: 'from-blue-500 to-cyan-500', label: 'Mavi (Orta)', bg: 'bg-blue-50' },
    { value: 'from-purple-500 to-pink-500', label: 'Mor (Orta)', bg: 'bg-purple-50' },
    { value: 'from-green-500 to-emerald-500', label: 'Yeşil (Orta)', bg: 'bg-green-50' },
    { value: 'from-orange-500 to-red-500', label: 'Turuncu (Orta)', bg: 'bg-orange-50' }
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Paketleri getir
      const packagesResponse = await fetch(API_URLS.ADMIN.HOSTING_PACKAGES);
      const packagesResult = await packagesResponse.json();
      
      if (packagesResult.status === 'success') {
        setPackages(packagesResult.data);
      } else {
        setError('Paketler yüklenirken hata oluştu');
      }
    } catch (error) {
      setError('Veriler yüklenirken hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      price_monthly: '',
      price_yearly: '',
      original_price_monthly: '',
      original_price_yearly: '',
      discount: 0,
      color: 'from-blue-500 to-cyan-500',
      bg_color: 'bg-blue-50',
      icon: 'Server',
      is_popular: false,
      description: '',
      features: [{ name: '', included: true }],
      additional_features: [{ name: '', included: true }],
      specs: { websites: '', ram: '', cpu: '', storage: '' },
      sort_order: 0,
      url: ''
    });
    setEditingPackage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const url = editingPackage 
        ? `${API_URLS.ADMIN.UPDATE_HOSTING_PACKAGE}&package_id=${editingPackage.id}`
        : API_URLS.ADMIN.CREATE_HOSTING_PACKAGE;

      const method = editingPackage ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.status === 'success') {
        setIsDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        setError(result.message || 'İşlem başarısız');
      }
    } catch (error) {
      setError('İşlem sırasında hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      price_monthly: pkg.price_monthly,
      price_yearly: pkg.price_yearly,
      original_price_monthly: pkg.original_price_monthly,
      original_price_yearly: pkg.original_price_yearly,
      discount: pkg.discount,
      color: pkg.color,
      bg_color: pkg.bg_color,
      icon: pkg.icon,
      is_popular: pkg.is_popular == 1 || pkg.is_popular === "1" || pkg.is_popular === true,
      description: pkg.description,
      features: Array.isArray(pkg.features) ? pkg.features : [{ name: '', included: true }],
      additional_features: Array.isArray(pkg.additional_features) ? pkg.additional_features : [{ name: '', included: true }],
      specs: pkg.specs || { websites: '', ram: '', cpu: '', storage: '' },
      sort_order: pkg.sort_order,
      url: pkg.url || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (packageId) => {
    if (!confirm('Bu paketi silmek istediğinizden emin misiniz?')) return;

    try {
              const response = await fetch(`${API_URLS.ADMIN.DELETE_HOSTING_PACKAGE}&package_id=${packageId}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (result.status === 'success') {
        fetchData();
      } else {
        setError(result.message || 'Silme işlemi başarısız');
      }
    } catch (error) {
      setError('Silme sırasında hata oluştu: ' + error.message);
    }
  };

  const handleToggleStatus = async (packageId) => {
    try {
              const response = await fetch(`${API_URLS.ADMIN.TOGGLE_HOSTING_PACKAGE_STATUS}&package_id=${packageId}`, {
        method: 'PATCH'
      });

      const result = await response.json();

      if (result.status === 'success') {
        fetchData();
      } else {
        setError(result.message || 'Durum değiştirme başarısız');
      }
    } catch (error) {
      setError('Durum değiştirme sırasında hata oluştu: ' + error.message);
    }
  };

  const addFeature = (type) => {
    setFormData(prev => ({
      ...prev,
      [type]: [...prev[type], { name: '', included: true }]
    }));
  };

  const removeFeature = (type, index) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const updateFeature = (type, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [type]: prev[type].map((feature, i) => 
        i === index ? { ...feature, [field]: value } : feature
      )
    }));
  };

  // İkon component'ini döndüren yardımcı fonksiyon
  const getIconComponent = (iconName) => {
    const iconMap = {
      'Server': Server,
      'Settings': Settings,
      'Monitor': Monitor,
      'Package': Package,
      'Globe': Globe,
      'Shield': Shield,
      'Zap': Zap,
      'Crown': Crown,
      'TrendingUp': TrendingUp,
      'Building': Building,
      'Code': Code
    };
    return iconMap[iconName] || Server;
  };

  if (loading && packages.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6 overflow-x-hidden">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="w-full sm:w-auto">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">Hosting Paketleri Yönetimi</h1>
          <p className="text-sm sm:text-base text-gray-600 break-words">Web hosting paketlerini düzenleyin</p>
        </div>
        
        <div className="w-full sm:w-auto">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Paket Ekle
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-[95vw] sm:max-w-md md:max-w-2xl lg:max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-base sm:text-lg">
                  {editingPackage ? 'Paketi Düzenle' : 'Yeni Paket Ekle'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-sm break-words">{error}</AlertDescription>
                  </Alert>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="name" className="text-sm">Paket Adı</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="WH-Başlangıç Paket"
                      required
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="sort_order" className="text-sm">Sıralama</Label>
                    <Input
                      id="sort_order"
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) }))}
                      placeholder="0"
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="price_monthly" className="text-sm">Aylık Fiyat (TL)</Label>
                    <Input
                      id="price_monthly"
                      type="number"
                      step="0.01"
                      value={formData.price_monthly}
                      onChange={(e) => setFormData(prev => ({ ...prev, price_monthly: parseFloat(e.target.value) }))}
                      placeholder="89.00"
                      required
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="price_yearly" className="text-sm">Yıllık Fiyat (TL)</Label>
                    <Input
                      id="price_yearly"
                      type="number"
                      step="0.01"
                      value={formData.price_yearly}
                      onChange={(e) => setFormData(prev => ({ ...prev, price_yearly: parseFloat(e.target.value) }))}
                      placeholder="890.00"
                      required
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="original_price_monthly" className="text-sm">Orijinal Aylık Fiyat (TL)</Label>
                    <Input
                      id="original_price_monthly"
                      type="number"
                      step="0.01"
                      value={formData.original_price_monthly}
                      onChange={(e) => setFormData(prev => ({ ...prev, original_price_monthly: parseFloat(e.target.value) }))}
                      placeholder="119.00"
                      required
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="original_price_yearly" className="text-sm">Orijinal Yıllık Fiyat (TL)</Label>
                    <Input
                      id="original_price_yearly"
                      type="number"
                      step="0.01"
                      value={formData.original_price_yearly}
                      onChange={(e) => setFormData(prev => ({ ...prev, original_price_yearly: parseFloat(e.target.value) }))}
                      placeholder="1190.00"
                      required
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="discount" className="text-sm">İndirim (%)</Label>
                    <Input
                      id="discount"
                      type="number"
                      value={formData.discount}
                      onChange={(e) => setFormData(prev => ({ ...prev, discount: parseInt(e.target.value) }))}
                      placeholder="25"
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="icon" className="text-sm">İkon</Label>
                    <select
                      id="icon"
                      value={formData.icon}
                      onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                      className="w-full p-2 border rounded-md"
                    >
                      {iconOptions.map(icon => (
                        <option key={icon} value={icon}>{icon}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="package_color" className="text-sm">Paket Rengi</Label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2 mt-2">
                      {packageColorOptions.map((color) => (
                        <button
                          key={color.value}
                          type="button"
                          onClick={() => setFormData(prev => ({ 
                            ...prev, 
                            color: color.value,
                            bg_color: color.bg
                          }))}
                          className={`w-full h-8 rounded-lg border-2 transition-all bg-gradient-to-r ${
                            formData.color === color.value
                              ? 'border-gray-800 scale-110'
                              : 'border-gray-300 hover:border-gray-500'
                          } ${color.value}`}
                          title={color.label}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 break-words">
                      Seçili: {packageColorOptions.find(c => c.value === formData.color)?.label || 'Mavi'}
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_popular"
                      checked={formData.is_popular}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_popular: checked }))}
                    />
                    <Label htmlFor="is_popular" className="text-sm">Popüler Paket</Label>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="description" className="text-sm">Açıklama</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Paket açıklaması"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <Label htmlFor="url" className="text-sm">URL (Paket Sayfası Linki)</Label>
                  <Input
                    id="url"
                    type="text"
                    value={formData.url || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="/hosting veya https://example.com/paket"
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1 break-words">Paket detay sayfası veya satın alma sayfası URL'si</p>
                </div>
                
                <div>
                  <Label className="text-sm">Özellikler</Label>
                  <div className="space-y-2 mt-2">
                    {formData.features.map((feature, index) => (
                      <div key={index} className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:space-x-2">
                        <div className="flex items-center gap-2 sm:gap-2 flex-1 w-full sm:w-auto">
                          {feature.included ? (
                            <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <X className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                          <Input
                            value={feature.name}
                            onChange={(e) => updateFeature('features', index, 'name', e.target.value)}
                            placeholder="Özellik adı"
                            className={`flex-1 min-w-0 ${!feature.included ? 'text-gray-400 line-through' : ''}`}
                          />
                        </div>
                        <div className="flex items-center gap-2 self-end sm:self-auto">
                          <Switch
                            checked={feature.included}
                            onCheckedChange={(checked) => updateFeature('features', index, 'included', checked)}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => removeFeature('features', index)}
                            className="flex-shrink-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => addFeature('features')}
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Özellik Ekle
                    </Button>
                  </div>
                </div>
                
                <div>
                  <Label className="text-sm">Ek Özellikler</Label>
                  <div className="space-y-2 mt-2">
                    {formData.additional_features.map((feature, index) => (
                      <div key={index} className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:space-x-2">
                        <div className="flex items-center gap-2 sm:gap-2 flex-1 w-full sm:w-auto">
                          {feature.included ? (
                            <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <X className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                          <Input
                            value={feature.name}
                            onChange={(e) => updateFeature('additional_features', index, 'name', e.target.value)}
                            placeholder="Ek özellik adı"
                            className={`flex-1 min-w-0 ${!feature.included ? 'text-gray-400 line-through' : ''}`}
                          />
                        </div>
                        <div className="flex items-center gap-2 self-end sm:self-auto">
                          <Switch
                            checked={feature.included}
                            onCheckedChange={(checked) => updateFeature('additional_features', index, 'included', checked)}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => removeFeature('additional_features', index)}
                            className="flex-shrink-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => addFeature('additional_features')}
                      className="w-full"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ek Özellik Ekle
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="specs_websites" className="text-sm">Web Sitesi Sayısı</Label>
                    <Input
                      id="specs_websites"
                      value={formData.specs.websites}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        specs: { ...prev.specs, websites: e.target.value }
                      }))}
                      placeholder="1"
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="specs_ram" className="text-sm">RAM</Label>
                    <Input
                      id="specs_ram"
                      value={formData.specs.ram}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        specs: { ...prev.specs, ram: e.target.value }
                      }))}
                      placeholder="1 GB"
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="specs_cpu" className="text-sm">CPU</Label>
                    <Input
                      id="specs_cpu"
                      value={formData.specs.cpu}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        specs: { ...prev.specs, cpu: e.target.value }
                      }))}
                      placeholder="1 Core"
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="specs_storage" className="text-sm">Depolama</Label>
                    <Input
                      id="specs_storage"
                      value={formData.specs.storage}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        specs: { ...prev.specs, storage: e.target.value }
                      }))}
                      placeholder="10 GB NVMe"
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:space-x-2 sm:space-y-0">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="w-full sm:w-auto"
                  >
                    İptal
                  </Button>
                  <Button type="submit" disabled={loading} className="w-full sm:w-auto">
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {editingPackage ? 'Güncelle' : 'Ekle'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm break-words">{error}</AlertDescription>
        </Alert>
      )}

      {/* Paketler */}
      <div className="grid gap-3 sm:gap-4">
        {packages.map((pkg) => (
          <Card key={pkg.id} className="relative overflow-hidden">
            <CardHeader className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-3 sm:gap-0">
                                 <div className="flex items-center space-x-3 flex-1 min-w-0">
                   <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-r ${pkg.color} flex items-center justify-center flex-shrink-0`}>
                     {(() => {
                       const IconComponent = getIconComponent(pkg.icon);
                       return <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-white" />;
                     })()}
                   </div>
                  <div className="min-w-0 flex-1">
                    <CardTitle className="flex flex-wrap items-center gap-2 text-base sm:text-lg">
                      <span className="break-words">{pkg.name}</span>
                      {(pkg.is_popular == 1 || pkg.is_popular === "1" || pkg.is_popular === true) && (
                        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 text-xs">
                          Popüler
                        </Badge>
                      )}
                      {!pkg.is_active && (
                        <Badge variant="destructive" className="text-xs">Pasif</Badge>
                      )}
                    </CardTitle>
                                        <p className="text-xs sm:text-sm text-gray-600 break-words">Sınırsız Hosting Paketi</p>
                     <div className="flex items-center space-x-2 mt-1">
                       <div className={`w-4 h-4 rounded bg-gradient-to-r ${pkg.color}`}></div>
                       <span className="text-xs text-gray-500 break-words">
                         {packageColorOptions.find(c => c.value === pkg.color)?.label || 'Mavi'}
                       </span>
                     </div>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-2 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleStatus(pkg.id)}
                    className="w-full sm:w-auto"
                  >
                    {pkg.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(pkg)}
                    className="w-full sm:w-auto"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(pkg.id)}
                    className="w-full sm:w-auto"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="p-4 sm:p-6 pt-0">
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4">
                <div className="flex items-center space-x-2">
                  <Package className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="text-xs sm:text-sm break-words">{pkg.specs?.websites || 'N/A'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Server className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="text-xs sm:text-sm break-words">{pkg.specs?.ram || 'N/A'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Settings className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="text-xs sm:text-sm break-words">{pkg.specs?.cpu || 'N/A'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Monitor className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="text-xs sm:text-sm break-words">{pkg.specs?.storage || 'N/A'}</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-base sm:text-lg font-bold text-green-600 break-words">{pkg.price_monthly} ₺</span>
                  {pkg.original_price_monthly > pkg.price_monthly && (
                    <span className="text-xs sm:text-sm text-gray-500 line-through break-words">{pkg.original_price_monthly} ₺</span>
                  )}
                  {pkg.discount > 0 && (
                    <Badge variant="secondary" className="text-xs">%{pkg.discount} İndirim</Badge>
                  )}
                </div>
                
                <div className="text-xs sm:text-sm text-gray-600 break-words">
                  Yıllık: {pkg.price_yearly} ₺
                </div>
              </div>
              
              {Array.isArray(pkg.features) && pkg.features.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2 break-words">Özellikler:</h4>
                  <div className="flex flex-wrap gap-1 sm:gap-2">
                    {pkg.features.slice(0, 5).map((feature, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {feature.name}
                      </Badge>
                    ))}
                    {pkg.features.length > 5 && (
                      <Badge variant="outline" className="text-xs">
                        +{pkg.features.length - 5} daha
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      
      {packages.length === 0 && !loading && (
        <Card>
          <CardContent className="text-center py-8">
            <Server className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz Hosting Paketi Yok</h3>
            <p className="text-gray-600 mb-4">İlk hosting paketinizi oluşturmak için yukarıdaki butona tıklayın.</p>
            <Button onClick={() => { resetForm(); setIsDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              İlk Paketi Ekle
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminHostingPackages;
